import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useApi } from '../composables/useApi'
import { log } from '../utils/logger'
import { sanitizeUserData } from '../utils/sanitize'
import type { User } from '../types/user'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null)
  const token = ref<string | null>(null)
  const isLoading = ref(false)

  // Get API instance
  const { postData, putData } = useApi()

  // Computed
  const isAuthenticated = computed(() => !!token.value && !!user.value)
  const userRole = computed(() => user.value?.role || null)
  const isAdmin = computed(() => userRole.value === 'admin')
  const isEditor = computed(() => userRole.value === 'editor' || userRole.value === 'admin')

  // Actions
  const login = async (email: string, password: string) => {
    isLoading.value = true
    try {
      const data = await postData<{ token: string; user: User }>('/api/auth/login', {
        email,
        password,
      })

      if (data) {
        // Store token
        token.value = data.token
        localStorage.setItem('auth_token', data.token)

        // Sanitize and store user data (protect against XSS from API)
        const sanitizedUser = sanitizeUserData(data.user as unknown as Record<string, unknown>)
        user.value = sanitizedUser as unknown as User
        localStorage.setItem('auth_user', JSON.stringify(sanitizedUser))

        return { success: true }
      } else {
        return { success: false, error: 'Login failed' }
      }
    } catch (error) {
      log.error('Login error:', error)
      return { success: false, error: error instanceof Error ? error.message : 'Login failed' }
    } finally {
      isLoading.value = false
    }
  }

  const logout = () => {
    user.value = null
    token.value = null
    localStorage.removeItem('auth_token')
    localStorage.removeItem('auth_user')
    
    // Force clear development bypass on logout
    if (window.location.hostname === 'localhost') {
      localStorage.removeItem('dev_auth_bypass')
    }
  }

  const checkAuth = async () => {
    if (!token.value) return false

    // Development bypass: Skip token verification for test token
    if (window.location.hostname === 'localhost' && token.value === 'test') {
      // log.debug('🔧 Development mode: Skipping token verification for test token')
      return true
    }

    isLoading.value = true
    try {
      // Use centralized API system now that useApi handles auth verify correctly
      const { fetchData } = useApi()
      const data = await fetchData('/api/auth/verify')
      if (
        data &&
        typeof data === 'object' &&
        'user' in data &&
        data.user &&
        typeof data.user === 'object'
      ) {
        // Sanitize user data from API before storing
        const sanitizedUser = sanitizeUserData(data.user as unknown as Record<string, unknown>)
        user.value = sanitizedUser as unknown as User
        localStorage.setItem('auth_user', JSON.stringify(sanitizedUser))
        return true
      } else {
        // Token is invalid or expired
        logout()
        return false
      }
    } catch (error) {
      log.error('Auth check error:', error)
      logout()
      return false
    } finally {
      isLoading.value = false
    }
  }

  const register = async (userData: { email: string; password: string; name: string }) => {
    isLoading.value = true
    try {
      const data = await postData('/api/auth/register', userData)

      if (data) {
        return { success: true, data }
      } else {
        return { success: false, error: 'Registration failed' }
      }
    } catch (error) {
      log.error('Registration error:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Registration failed',
      }
    } finally {
      isLoading.value = false
    }
  }

  const updateProfile = async (updates: Partial<User>) => {
    if (!token.value) return { success: false, error: 'Not authenticated' }

    isLoading.value = true
    try {
      const data = await putData<{ user: User }>('/api/auth/profile', updates)

      if (data) {
        user.value = { ...user.value, ...data.user }
        localStorage.setItem('auth_user', JSON.stringify(user.value))
        return { success: true, data }
      } else {
        return { success: false, error: 'Profile update failed' }
      }
    } catch (error) {
      log.error('Profile update error:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Profile update failed',
      }
    } finally {
      isLoading.value = false
    }
  }

  const changePassword = async (currentPassword: string, newPassword: string) => {
    if (!token.value) return { success: false, error: 'Not authenticated' }

    isLoading.value = true
    try {
      const data = await postData('/api/auth/change-password', { currentPassword, newPassword })

      if (data) {
        return { success: true }
      } else {
        return { success: false, error: 'Password change failed' }
      }
    } catch (error) {
      log.error('Password change error:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Password change failed',
      }
    } finally {
      isLoading.value = false
    }
  }

  const socialLogin = async (provider: string, socialData: any) => {
    isLoading.value = true
    try {
      const data = await postData<{ token: string; user: User }>(
        `/api/auth/${provider}`,
        socialData,
      )

      if (data) {
        // Store token
        token.value = data.token
        localStorage.setItem('auth_token', data.token)

        // Sanitize and store user data (protect against XSS from API)
        const sanitizedUser = sanitizeUserData(data.user as unknown as Record<string, unknown>)
        user.value = sanitizedUser as unknown as User
        localStorage.setItem('auth_user', JSON.stringify(sanitizedUser))

        return { success: true }
      } else {
        return { success: false, error: `${provider} login failed` }
      }
    } catch (error) {
      log.error(`${provider} login error:`, error)
      return {
        success: false,
        error: error instanceof Error ? error.message : `${provider} login failed`,
      }
    } finally {
      isLoading.value = false
    }
  }

  // Initialize auth state on app start
  const initialize = async () => {
    // Development bypass: Only set test token if explicitly requested and no existing auth
    if (window.location.hostname === 'localhost' && 
        !localStorage.getItem('auth_token') && 
        !localStorage.getItem('dev_auth_bypass_cleared')) {
      
    }

    // Restore token and user from localStorage on app start
    const storedToken = localStorage.getItem('auth_token')
    const storedUser = localStorage.getItem('auth_user')

    if (storedToken && storedUser) {
      try {
        token.value = storedToken
        // CRITICAL: Sanitize user data from localStorage before using it
        // localStorage can be manipulated by XSS attacks
        const parsedUser = JSON.parse(storedUser)
        user.value = sanitizeUserData(parsedUser as unknown as Record<string, unknown>) as unknown as User

        // Verify the token is still valid (skip for development test token)
        if (storedToken !== 'test') {
          await checkAuth()
        }
      } catch (error) {
        log.error('❌ Error restoring auth from localStorage:', error)
        logout()
      }
    }
  }


  return {
    // State
    user,
    token,
    isLoading,

    // Computed
    isAuthenticated,
    userRole,
    isAdmin,
    isEditor,

    // Actions
    login,
    logout,
    checkAuth,
    register,
    updateProfile,
    changePassword,
    socialLogin,
    initialize,
  }
})
